import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api.js';
import { URLS, ImgURL } from '../utils/urls.js';
import Tooltip from "../components/Tooltip.jsx";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  User,
  Trash2,
  UserMinus,
 CircleStar,
 CircleEllipsis,
 BookmarkX,
} from 'lucide-react';
import {  useNavigate } from 'react-router-dom';
import { useTopLoader } from "../context/TopLoaderContext";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryString, setSearchQueryString] = useState('');
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [filters, setFilters] = useState({ status: '', personality: '', interests: '' });
      const { start, stop } = useTopLoader();

  const navigate = useNavigate();
  const limit = 10;

  // ✅ Fetch users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    start();
    try {
      const res = await api.get(URLS.GET_USERS, {
        params: { page, limit, filters: JSON.stringify(filters),searchQuery: searchQueryString},
      });

      console.log('Filters:', { searchQuery, ...filters });
      setUsers(res.data.users || []);
      setTotalPages(res.data.totalPages || 1);
      stop();
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to fetch users. Please try again.');
      stop();
    } finally {
      setLoading(false);
       stop();
    }
  }, [page, limit, searchQueryString, filters]);

  // ✅ Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchUsers();
    }, 400);
    return () => clearTimeout(timeout);
  }, [fetchUsers]);

  // Select all
  const handleSelectAll = (e) => {
    const newSelected = new Set();
    if (e.target.checked) {
      users.forEach((user) => newSelected.add(user.id));
    }
    setSelectedUsers(newSelected);
    setIsAllSelected(e.target.checked);
  };

  // Select single
  const handleSelectUser = (e, userId) => {
    e.stopPropagation(); // prevent row click
    const newSelected = new Set(selectedUsers);
    if (e.target.checked) newSelected.add(userId);
    else newSelected.delete(userId);
    setSelectedUsers(newSelected);
  };

  // Actions
  const handleAction = async (action, userIds) => {
    const confirmMessage = `Are you sure you want to ${action} ${userIds.length} user(s)?`;
    if (!window.confirm(confirmMessage)) return;

    try {
      if (action === 'delete') {
        for (const id of userIds) {
          await api.delete(`/api/admin-panel/users/${id}`);
        }
      } else if (action === 'block' || action === 'unblock') {
        const isBlocked = action === 'block';
        for (const id of userIds) {
          await api.patch(`/api/admin-panel/users/${id}`, { isBlocked });
        }
      }
      fetchUsers();
      setSelectedUsers(new Set());
      setIsAllSelected(false);
    } catch (err) {
      console.error(`Error performing ${action}:`, err);
      alert(`Failed to ${action} user(s).`);
    }
  };

  return (
    <div className="p-2 bg-gray-100 min-h-screen font-sans">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">User Management</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchQueryString(searchQuery);
              }
            }}
            className="py-2 pr-10 pl-4 rounded-full border border-gray-300 focus:ring-2 focus:ring-[#db8654] focus:border-transparent transition-all duration-300 shadow-sm"
          />
          <Search
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black cursor-pointer"
            size={20}
            onClick = {() => setSearchQueryString(searchQuery)}
            />
        </div>
      </div>

      {/* Filters + Bulk actions */}
      <div className="flex justify-between items-center mb-4">
        {/* Filters */}
        <div className="flex gap-4">

        <select
        value={filters.personality_type || ""}   // 👈 match the correct key
        onChange={(e) =>
            setFilters({
            ...filters,
            personality_type: e.target.value || undefined, // clear when "All" is chosen
            })
        }
        className="px-3 py-2 rounded-lg border border-gray-300"
        >
        <option value="">All Personalities</option>
        <option value="The Nurturer">The Nurturer</option>
        <option value="The Go-Getter">The Go-Getter</option>
        <option value="The Socialite">The Socialite</option>
        <option value="The Deep Soul">The Deep Soul</option>
        <option value="The Soft Life Curator">The Soft Life Curator</option>
        <option value="The Explorer">The Explorer</option>
        <option value="The Visionary">The Visionary</option>
        <option value="The Grounded One">The Grounded One</option>
        </select>


            <div className="flex gap-4 items-center">
            {/* Subscribed */}
            <label className="flex items-center gap-2">
                <input
                type="checkbox"
                checked={filters.subscription_status === 1}
                onChange={(e) =>
                    setFilters({
                    ...filters,
                    subscription_status: e.target.checked ? 1 : undefined,
                    })
                }
                className="rounded text-[#db8654] focus:ring-[#db8654] border-gray-300"
                />
                Subscribed
            </label>

            {/* Blocked */}
            <label className="flex items-center gap-2">
                <input
                type="checkbox"
                checked={filters.is_blocked === 1}
                onChange={(e) =>
                    setFilters({
                    ...filters,
                    is_blocked: e.target.checked ? 1 : undefined,
                    })
                }
                className="rounded text-[#db8654] focus:ring-[#db8654] border-gray-300"
                />
                Blocked
            </label>

            {/* Not Verified */}
            <label className="flex items-center gap-2">
                <input
                type="checkbox"
                checked={filters.is_verified === 0}
                onChange={(e) =>
                    setFilters({
                    ...filters,
                    is_verified: e.target.checked ? 0 : undefined,
                    })
                }
                className="rounded text-[#db8654] focus:ring-[#db8654] border-gray-300"
                />
                Not Verified
            </label>
            </div>
        </div>

        {/* Bulk actions (only visible if something is selected) */}
        {selectedUsers.size > 0 && (
          <div className="flex items-center gap-4">
            <span className="text-gray-600 font-medium">Selected: {selectedUsers.size}</span>
            <button
              onClick={() => handleAction('delete', Array.from(selectedUsers))}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <Trash2 size={16} /> Delete
            </button>
            <button
              onClick={() => handleAction('block', Array.from(selectedUsers))}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"
            >
              <UserMinus size={16} /> Block
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-x-auto">
        {loading && <div className="p-8 text-center text-gray-500">Loading users...</div>}
        {error && <div className="p-8 text-center text-red-500">{error}</div>}
        {!loading && !error && (
<table className="min-w-[700px] w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th className="p-2 sm:p-4">
          <input
            type="checkbox"
            onChange={handleSelectAll}
            checked={isAllSelected}
            className="rounded text-[#db8654] focus:ring-[#db8654] border-gray-300"
          />
        </th>
        <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase min-w-[150px]">
          User
        </th>
        <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase min-w-[180px]">
          Email
        </th>
        <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase min-w-[140px]">
          Personality
        </th>
        <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase min-w-[100px]">
          Status
        </th>
        <th className="p-2 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-500 uppercase min-w-[120px]">
          Registered
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {users.length > 0 ? (
        users.map((user) => (
          <tr
            key={user.id}
            className="hover:bg-gray-50 transition-colors cursor-pointer"
            onClick={() => navigate(`/admin/users/${user.id}`)}
          >
            <td className="p-1 sm:p-2 text-center" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selectedUsers.has(user.id)}
                onChange={(e) => handleSelectUser(e, user.id)}
                className="rounded text-[#db8654] focus:ring-[#db8654] border-gray-300"
              />
            </td>
            <td className="p-1 sm:p-2 flex items-center gap-2 sm:gap-3 whitespace-nowrap">
              {user.profilePhotoUrl ? (
                <img
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover border"
                  src={ImgURL(user.thumb150 || user.profilePhotoUrl)}
                  alt={user.firstName}
                />              ) : (
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                  <User size={16} />
                </div>
              )}
              <span className="text-gray-900 font-medium text-sm sm:text-base truncate">
                {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
              </span>
              {user.subscriptionStatus && (
                <Tooltip text="Subscribed User">
                  <CircleStar size={18} color="green" />
                </Tooltip>
              )}
              {user.isBlocked == 1 && (
                <Tooltip text="Blocked User">
                  <BookmarkX size={18} color="red" />
                </Tooltip>
              )}
              {user.isVerified == 0 && (
                <Tooltip text="User is not Verified">
                  <CircleEllipsis size={18} color="orange" />
                </Tooltip>
              )}
            </td>
            <td className="p-1 sm:p-2 text-gray-500 text-sm sm:text-base truncate">{user.email}</td>
            <td className="p-1 sm:p-2 text-gray-500 text-sm sm:text-base whitespace-nowrap">{user.personalityType || 'N/A'}</td>
            <td className="p-1 sm:p-2">
              {user.isBlocked ? (
                <span className="px-2 py-1 text-xs sm:text-sm rounded-full bg-red-100 text-red-700 whitespace-nowrap">
                  Blocked
                </span>
              ) : (
                <span className="px-2 py-1 text-xs sm:text-sm rounded-full bg-green-100 text-green-700 whitespace-nowrap">
                  Active
                </span>
              )}
            </td>
            <td className="p-1 sm:p-2 text-gray-500 text-sm sm:text-base whitespace-nowrap">
              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan="6" className="text-center p-4 text-gray-500 text-sm sm:text-base">
            No users found.
          </td>
        </tr>
      )}
    </tbody>
  </table>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-6 flex justify-between items-center">
        <button
          onClick={() => setPage((p) => p - 1)}
          disabled={page <= 1}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronLeft size={16} /> Previous
        </button>
        <span className="text-gray-600">
          Page <span className="font-semibold">{page}</span> of{' '}
          <span className="font-semibold">{totalPages}</span>
        </span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= totalPages}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Next <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default UsersPage;
